import { BACKEND_URL } from "../config";
import React, { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';
import axios from 'axios';

const TradeDetailModal = ({ trade, onClose }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();
  const seriesRef = useRef();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveTradeData, setLiveTradeData] = useState(null);
  const [loadingLiveData, setLoadingLiveData] = useState(false);

  const API = BACKEND_URL;

  // Fetch live trade data (current price, P/L, etc.)
  useEffect(() => {
    if (!trade || trade.status === 'CLOSED' || trade.status === 'LOST' || trade.status === 'WON') return;

    // RACE CONDITION FIX: Track the current trade_id to prevent stale updates
    const currentTradeId = trade.trade_id;
    let isCancelled = false;

    const fetchLiveData = async () => {
      setLoadingLiveData(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(
          `${API}/api/trade-detail/${currentTradeId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Only update if this effect hasn't been cancelled (trade changed)
        if (!isCancelled) {
          setLiveTradeData(response.data);
          console.log('✅ Live trade data loaded for:', currentTradeId);
        } else {
          console.log('⚠️ Ignoring stale live data for:', currentTradeId);
        }
      } catch (err) {
        console.error('❌ Error fetching live trade data:', err);
      } finally {
        if (!isCancelled) {
          setLoadingLiveData(false);
        }
      }
    };

    fetchLiveData();
    // Refresh every 30 seconds for active/pending trades
    const interval = setInterval(fetchLiveData, 30000);
    
    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [trade, API]);

  // Fetch historical chart data
  useEffect(() => {
    if (!trade) return;

    // RACE CONDITION FIX: Track the current trade_id to prevent stale updates
    const currentTradeId = trade.trade_id;
    let isCancelled = false;

    const fetchChartData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('auth_token');
        console.log('📊 Fetching chart data for trade:', currentTradeId);
        console.log('🔑 Token exists:', !!token);
        
        const response = await axios.get(
          `${API}/api/trade-chart-data/${currentTradeId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Only update if this effect hasn't been cancelled (trade changed)
        if (isCancelled) {
          console.log('⚠️ Ignoring stale chart data for:', currentTradeId);
          return;
        }

        console.log('📊 Chart response:', response.data);

        if (response.data.success) {
          setChartData(response.data.candlestick_data);
          console.log('✅ Chart data loaded:', response.data.candlestick_data.length, 'candles');
        } else if (response.data.error === 'API_RATE_LIMIT') {
          setError('⏳ Chart temporarily unavailable (API rate limit). Trade details shown below. Try again in 1 minute.');
          console.warn('⚠️ API rate limit hit');
        } else {
          const errorMsg = response.data.message || 'Failed to load chart data';
          setError(errorMsg);
          console.error('❌ Chart error:', errorMsg);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('❌ Error fetching chart data:', err);
          console.error('❌ Error details:', err.response?.data);
          setError(`Error loading chart data: ${err.response?.data?.detail || err.message}`);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchChartData();
    
    return () => {
      isCancelled = true;
    };
  }, [trade, API]);

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current || chartData.length === 0) return;

    // Create chart with same styling as existing chart
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 500,
      layout: {
        background: { color: '#0f172a' },
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: '#1e293b' },
        horzLines: { color: '#1e293b' },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderColor: '#334155',
      },
      timeScale: {
        borderColor: '#334155',
        timeVisible: true,
        secondsVisible: false,
      },
    });

    // V5.x API uses addSeries instead of addCandlestickSeries
    const candlestickSeries = chart.addSeries({
      type: 'Candlestick',
      upColor: '#10b981',
      downColor: '#ef4444',
      borderUpColor: '#10b981',
      borderDownColor: '#ef4444',
      wickUpColor: '#10b981',
      wickDownColor: '#ef4444',
    });

    candlestickSeries.setData(chartData);

    // Add price lines
    // Entry line (Blue)
    const entryLine = candlestickSeries.createPriceLine({
      price: trade.entry_price,
      color: '#3b82f6',
      lineWidth: 2,
      lineStyle: 0, // Solid
      axisLabelVisible: true,
      title: `Entry: ${trade.entry_price.toFixed(5)}`,
    });

    // Stop Loss line (Red)
    const slLine = candlestickSeries.createPriceLine({
      price: trade.stop_loss,
      color: '#ef4444',
      lineWidth: 2,
      lineStyle: 0, // Solid
      axisLabelVisible: true,
      title: `SL: ${trade.stop_loss.toFixed(5)}`,
    });

    // Original SL if trailed (Dotted Red)
    if (trade.original_stop_loss && trade.original_stop_loss !== trade.stop_loss) {
      candlestickSeries.createPriceLine({
        price: trade.original_stop_loss,
        color: '#ef4444',
        lineWidth: 1,
        lineStyle: 2, // Dotted
        axisLabelVisible: true,
        title: `Original SL: ${trade.original_stop_loss.toFixed(5)}`,
      });
    }

    // Take Profit line (Green)
    const tpLine = candlestickSeries.createPriceLine({
      price: trade.take_profit,
      color: '#10b981',
      lineWidth: 2,
      lineStyle: 0, // Solid
      axisLabelVisible: true,
      title: `TP: ${trade.take_profit.toFixed(5)}`,
    });

    // Close/Exit point (Yellow) - only for closed trades
    if (trade.status !== 'ACTIVE' && trade.status !== 'PENDING' && trade.close_price) {
      candlestickSeries.createPriceLine({
        price: trade.close_price,
        color: '#eab308',
        lineWidth: 3,
        lineStyle: 0, // Solid
        axisLabelVisible: true,
        title: `Close: ${trade.close_price.toFixed(5)}`,
      });
    }

    // For ACTIVE trades: Show profit/loss area between entry and current price
    if (trade.status === 'ACTIVE' && chartData.length > 0) {
      const currentPrice = chartData[chartData.length - 1].close;
      const entryPrice = trade.entry_price;
      const direction = trade.direction;
      
      // Determine if in profit based on direction
      let isInProfit = false;
      if (direction === 'BUY') {
        isInProfit = currentPrice > entryPrice;
      } else { // SELL
        isInProfit = currentPrice < entryPrice;
      }
      
      // Create a line series to fill the area between entry and current price
      const areaSeries = chart.addLineSeries({
        color: isInProfit ? '#10b981' : '#ef4444',
        lineWidth: 0,
        topColor: isInProfit ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)',
        bottomColor: isInProfit ? 'rgba(16, 185, 129, 0.0)' : 'rgba(239, 68, 68, 0.0)',
        priceLineVisible: false,
        lastValueVisible: false,
      });
      
      // Create data points to fill between entry and current price
      const fillData = chartData.map(candle => ({
        time: candle.time,
        value: direction === 'BUY' 
          ? (currentPrice > entryPrice ? currentPrice : entryPrice)
          : (currentPrice < entryPrice ? currentPrice : entryPrice)
      }));
      
      areaSeries.setData(fillData);
      
      // Add baseline series to show the filled area
      const baselineSeries = chart.addBaselineSeries({
        baseValue: { type: 'price', price: entryPrice },
        topLineColor: isInProfit ? '#10b981' : '#ef4444',
        topFillColor1: isInProfit ? 'rgba(16, 185, 129, 0.28)' : 'rgba(239, 68, 68, 0.28)',
        topFillColor2: isInProfit ? 'rgba(16, 185, 129, 0.05)' : 'rgba(239, 68, 68, 0.05)',
        bottomLineColor: isInProfit ? '#10b981' : '#ef4444',
        bottomFillColor1: isInProfit ? 'rgba(16, 185, 129, 0.05)' : 'rgba(239, 68, 68, 0.05)',
        bottomFillColor2: isInProfit ? 'rgba(16, 185, 129, 0.28)' : 'rgba(239, 68, 68, 0.28)',
        lineWidth: 0,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      
      // Set baseline data with current price
      const baselineData = chartData.map(candle => ({
        time: candle.time,
        value: candle.close
      }));
      
      baselineSeries.setData(baselineData);
      
      // Add current price marker
      candlestickSeries.createPriceLine({
        price: currentPrice,
        color: isInProfit ? '#10b981' : '#ef4444',
        lineWidth: 2,
        lineStyle: 1, // Dashed
        axisLabelVisible: true,
        title: `Current: ${currentPrice.toFixed(5)} ${isInProfit ? '📈' : '📉'}`,
      });
    }

    chartRef.current = chart;
    seriesRef.current = candlestickSeries;

    // Fit content
    chart.timeScale().fitContent();

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.resize(
          chartContainerRef.current.clientWidth,
          500
        );
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
      }
    };
  }, [chartData, trade]);

  if (!trade) {
    console.log('❌ TradeDetailModal: No trade prop provided');
    return null;
  }
  
  console.log('✅ TradeDetailModal: Rendering for trade', trade.trade_id);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'WON': return '#10b981';
      case 'LOST': return '#ef4444';
      case 'BREAKEVEN': return '#f59e0b';
      case 'ACTIVE': return '#3b82f6';
      case 'PENDING': return '#94a3b8';
      default: return '#64748b';
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '2rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#1e293b',
          borderRadius: '12px',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid #334155',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>
            {trade.pair} {trade.direction} - Trade Details
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.5rem',
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {/* Live Trade Status - For Active/Pending Trades */}
          {(trade.status === 'ACTIVE' || trade.status === 'PENDING') && liveTradeData && (
            <div style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)', padding: '1.5rem', borderRadius: '12px', border: '2px solid #4f46e5', marginBottom: '2rem' }}>
              <h4 style={{ color: 'white', fontSize: '1.125rem', fontWeight: '700', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                📊 Live Trade Status
                {loadingLiveData && <span style={{ fontSize: '0.75rem', color: '#a5b4fc' }}>(Updating...)</span>}
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                {/* Current Price */}
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1rem', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#c7d2fe', marginBottom: '0.25rem' }}>Current Price</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white' }}>
                    {liveTradeData.current_price?.toFixed(5) || 'Loading...'}
                  </div>
                  {liveTradeData.price_change && (
                    <div style={{ fontSize: '0.75rem', color: liveTradeData.price_change >= 0 ? '#86efac' : '#fca5a5', marginTop: '0.25rem' }}>
                      {liveTradeData.price_change >= 0 ? '↑' : '↓'} {Math.abs(liveTradeData.price_change).toFixed(5)} pips
                    </div>
                  )}
                </div>

                {/* Live P/L */}
                <div style={{ background: liveTradeData.live_profit_loss >= 0 ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)', padding: '1rem', borderRadius: '8px', border: `2px solid ${liveTradeData.live_profit_loss >= 0 ? '#22c55e' : '#ef4444'}` }}>
                  <div style={{ fontSize: '0.75rem', color: '#c7d2fe', marginBottom: '0.25rem' }}>
                    {liveTradeData.live_profit_loss >= 0 ? '💰 Profit' : '📉 Loss'}
                  </div>
                  <div style={{ fontSize: '1.75rem', fontWeight: '800', color: liveTradeData.live_profit_loss >= 0 ? '#22c55e' : '#ef4444' }}>
                    {liveTradeData.live_profit_loss >= 0 ? '+' : ''}${liveTradeData.live_profit_loss?.toFixed(2) || '0.00'}
                  </div>
                  {liveTradeData.percent_to_tp !== undefined && (
                    <div style={{ fontSize: '0.75rem', color: '#c7d2fe', marginTop: '0.25rem' }}>
                      {liveTradeData.percent_to_tp.toFixed(1)}% to TP
                    </div>
                  )}
                </div>

                {/* Stop Loss Status */}
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1rem', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#c7d2fe', marginBottom: '0.25rem' }}>Stop Loss</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '700', color: 'white' }}>
                    {trade.stop_loss?.toFixed(5)}
                  </div>
                  {trade.trailing_sl_active ? (
                    <div style={{ fontSize: '0.75rem', color: '#86efac', marginTop: '0.25rem', fontWeight: '600' }}>
                      ✅ Trailed {trade.stop_loss > trade.entry_price ? '(In Profit)' : trade.stop_loss === trade.entry_price ? '(Breakeven)' : ''}
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.75rem', color: '#fbbf24', marginTop: '0.25rem' }}>
                      🔒 Original SL
                    </div>
                  )}
                </div>

                {/* Entry vs Current */}
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1rem', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#c7d2fe', marginBottom: '0.25rem' }}>Entry Price</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '700', color: 'white' }}>
                    {trade.entry_price?.toFixed(5)}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#c7d2fe', marginTop: '0.25rem' }}>
                    {trade.direction} @ {trade.order_type || 'Market Order'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Chart - Show trade details even if chart fails */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: 'white', fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
              Price Chart ({trade.timeframe}) {loading && '(Loading...)'}
            </h3>
            
            {error && (
              <div style={{ 
                height: '200px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                color: '#fbbf24',
                background: '#1e293b',
                borderRadius: '8px',
                fontSize: '0.875rem',
                marginBottom: '1rem',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                <div>⚠️ {error}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  Trade details are shown below
                </div>
              </div>
            )}

            {!error && chartData.length === 0 && !loading && (
              <div style={{ 
                height: '200px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                color: '#94a3b8', 
                background: '#1e293b', 
                borderRadius: '8px' 
              }}>
                📊 Chart data not available for this trade
              </div>
            )}

            {!error && chartData.length > 0 && (
              <div ref={chartContainerRef} style={{ height: '500px', background: '#0f172a', borderRadius: '8px' }} />
            )}
          </div>

          {/* Trade Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {/* Basic Info */}
            <div style={{ background: '#0f172a', padding: '1.5rem', borderRadius: '8px' }}>
              <h4 style={{ color: 'white', fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Trade Info</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8' }}>Status:</span>
                  <span style={{ color: getStatusColor(trade.status), fontWeight: '600' }}>{trade.status}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8' }}>Trade Type:</span>
                  <span style={{ color: 'white' }}>{trade.trade_type}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8' }}>Direction:</span>
                  <span style={{ color: trade.direction === 'BUY' ? '#10b981' : '#ef4444', fontWeight: '600' }}>{trade.direction}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8' }}>Order Type:</span>
                  <span style={{ color: 'white' }}>{trade.order_type}</span>
                </div>
                {trade.probability !== null && trade.probability !== undefined && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#94a3b8' }}>Probability:</span>
                    <span style={{ color: trade.probability >= 80 ? '#10b981' : trade.probability >= 70 ? '#f59e0b' : '#ef4444', fontWeight: '600' }}>
                      {trade.probability}%
                    </span>
                  </div>
                )}
                {trade.trade_type === 'MT5_SYNCED' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#94a3b8' }}>Source:</span>
                    <span style={{ color: '#3b82f6', fontWeight: '600' }}>
                      📊 MT5 Manual Trade
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Price Levels */}
            <div style={{ background: '#0f172a', padding: '1.5rem', borderRadius: '8px' }}>
              <h4 style={{ color: 'white', fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Price Levels</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#3b82f6' }}>Entry:</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>{trade.entry_price?.toFixed(5) || 'N/A'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#ef4444' }}>Stop Loss:</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>{trade.stop_loss?.toFixed(5) || 'N/A'}</span>
                </div>
                {trade.original_stop_loss && trade.original_stop_loss !== trade.stop_loss && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#ef4444', opacity: 0.7 }}>Original SL:</span>
                    <span style={{ color: 'white', opacity: 0.7 }}>{trade.original_stop_loss?.toFixed(5)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#10b981' }}>Take Profit:</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>{trade.take_profit?.toFixed(5) || 'N/A'}</span>
                </div>
                {trade.close_price && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#eab308' }}>Close Price:</span>
                    <span style={{ color: 'white', fontWeight: '600' }}>{trade.close_price?.toFixed(5)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Risk & Profit */}
            <div style={{ background: '#0f172a', padding: '1.5rem', borderRadius: '8px' }}>
              <h4 style={{ color: 'white', fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Risk & Profit</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8' }}>Risk Amount:</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>${trade.risk_amount?.toFixed(2) || 'N/A'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8' }}>Lot Size:</span>
                  <span style={{ color: 'white' }}>{trade.lot_size?.toFixed(2) || 'N/A'}</span>
                </div>
                {trade.actual_profit_loss !== undefined && trade.actual_profit_loss !== null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#94a3b8' }}>Profit/Loss:</span>
                    <span style={{
                      color: trade.actual_profit_loss >= 0 ? '#10b981' : '#ef4444',
                      fontWeight: '700',
                      fontSize: '1rem'
                    }}>
                      {trade.actual_profit_loss >= 0 ? '+' : ''}${trade.actual_profit_loss?.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div style={{ background: '#0f172a', padding: '1.5rem', borderRadius: '8px' }}>
              <h4 style={{ color: 'white', fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Timeline</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8' }}>Calculated:</span>
                  <span style={{ color: 'white' }}>{formatDate(trade.calculated_at)}</span>
                </div>
                {trade.activated_at && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#94a3b8' }}>Activated:</span>
                    <span style={{ color: 'white' }}>{formatDate(trade.activated_at)}</span>
                  </div>
                )}
                {trade.outcome_time && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#94a3b8' }}>Closed:</span>
                    <span style={{ color: 'white' }}>{formatDate(trade.outcome_time)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Scenario */}
            {trade.scenario_outcome && trade.scenario_outcome !== 'MANUAL_MT5_TRADE' && (
              <div style={{ background: '#0f172a', padding: '1.5rem', borderRadius: '8px' }}>
                <h4 style={{ color: 'white', fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Scenario</h4>
                <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.5rem 1rem',
                    background: '#1e293b',
                    borderRadius: '6px',
                    color: '#fbbf24',
                    fontWeight: '600'
                  }}>
                    {trade.scenario_outcome.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
            )}
            {trade.trade_type === 'MT5_SYNCED' && (
              <div style={{ background: '#0f172a', padding: '1.5rem', borderRadius: '8px' }}>
                <h4 style={{ color: 'white', fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Trade Source</h4>
                <div style={{ fontSize: '0.875rem' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.5rem 1rem',
                    background: '#1e293b',
                    borderRadius: '6px',
                    color: '#3b82f6',
                    fontWeight: '600'
                  }}>
                    📊 MetaTrader 5 (Manual)
                  </span>
                  <div style={{ marginTop: '0.5rem', color: '#94a3b8', fontSize: '0.75rem' }}>
                    Synced from MT5. No probability/scenario data available.
                  </div>
                </div>
              </div>
            )}

            {/* Trailing History */}
            {trade.trailing_history && trade.trailing_history.length > 0 && (
              <div style={{ background: '#0f172a', padding: '1.5rem', borderRadius: '8px', gridColumn: 'span 2' }}>
                <h4 style={{ color: 'white', fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Trailing Stop History</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
                  {trade.trailing_history.map((event, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#1e293b', borderRadius: '4px' }}>
                      <span style={{ color: '#94a3b8' }}>{formatDate(event.timestamp)}</span>
                      <span style={{ color: 'white' }}>SL: {event.old_sl?.toFixed(5)} → {event.new_sl?.toFixed(5)}</span>
                      <span style={{ color: '#10b981' }}>Profit: {event.profit_ratio}:1</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeDetailModal;
